import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Card, CardContent, CardHeader, Stack } from '@mui/material'
import { Select, MultiLineChart, ChartExportWrapper } from 'components'
import { RootState } from 'store/store'
import { TIME_GRANULARITY, TCompositionData } from 'types/models/DataAnalysis'
import EmptyState from 'pages/DataAnalysis/components/EmptyState'
import {
  selectChartData,
  selectChartStatus,
} from 'store/selectors/dataAnalysisSelectors'
import {
  patchDraftFilters,
  applyAndFetchChart,
} from 'store/slices/dataAnalysisSlice'
import { tooltipStyles, tooltipLabelStyles } from 'components/charts/styles'
import DataAnalysisFilters from '../../components/DataAnalysisFilters/DataAnalysisFilters'
import {
  getTabByKey,
  granularityOptions,
  prettyAttr,
  TAB_KEYS,
} from '../../utils/consts'
import { DataTabsLayout, TrendChartStatus } from '../../layouts'
import { toCompositionsChartData } from '../../transforms'
import normalizeDataset from '../../utils/normalizeDataset'
import * as sxStyles from '../tabs.styles'

const CompositionsTooltip = ({ active, payload = [], label }: any) => {
  const normalized =
    payload.length && payload[0]?.payload
      ? payload
      : payload.map((row: any) => ({ payload: row }))

  if (active === false || !normalized.length) return null

  const row = normalized[0].payload
  const params: Array<{ name: string; avg: number | string; unit?: string }> =
    Array.isArray(row?.parameters)
      ? row.parameters
      : normalized.map((p: any) => ({
          name: p.name ?? p.dataKey,
          avg: p.value ?? p.payload?.value,
          unit: '',
        }))

  return (
    <div style={{ ...tooltipStyles, padding: 8 }}>
      <div style={{ ...tooltipLabelStyles, fontWeight: 'bold' }}>{label}</div>
      {params.map((p) => {
        const displayName = prettyAttr(p.name || '')
        const num = Number(p.avg)
        const valueText = Number.isFinite(num)
          ? num.toFixed(2)
          : String(p.avg ?? '')
        const unit = p.unit === '%' ? '%' : ''
        return (
          <div key={p.name} style={tooltipLabelStyles}>
            {`${displayName}: ${valueText}${unit}`}
          </div>
        )
      })}
    </div>
  )
}

export const Compositions = () => {
  const dispatch = useDispatch()
  const rawData = useSelector(selectChartData(TAB_KEYS.COMPOSITIONS))
  const status = useSelector(selectChartStatus(TAB_KEYS.COMPOSITIONS))
  const currentTab = getTabByKey(TAB_KEYS.COMPOSITIONS, 'blends')

  const draftFilters = useSelector(
    (state: RootState) => state.dataAnalysis.byKey[TAB_KEYS.COMPOSITIONS]?.draft
  )
  const granularity = draftFilters?.time_granularity ?? TIME_GRANULARITY.DAILY

  useEffect(() => {
    if (!draftFilters?.time_granularity) {
      dispatch(
        patchDraftFilters({
          key: TAB_KEYS.COMPOSITIONS,
          patch: { time_granularity: TIME_GRANULARITY.DAILY },
        })
      )
    }
  }, [dispatch, draftFilters])

  const data = useMemo(
    () => normalizeDataset<TCompositionData>(rawData as any),
    [rawData]
  )
  const { rows, series } = useMemo(() => toCompositionsChartData(data), [data])
  const hasAnyData = rows.length > 0

  return (
    <DataTabsLayout
      sidebar={
        <DataAnalysisFilters
          filtersList={currentTab?.filters ?? []}
          chartKey={TAB_KEYS.COMPOSITIONS}
        />
      }
      tabKey={TAB_KEYS.COMPOSITIONS}
      onApply={() =>
        dispatch(applyAndFetchChart({ key: TAB_KEYS.COMPOSITIONS }))
      }
    >
      <TrendChartStatus status={status} title={TAB_KEYS.COMPOSITIONS}>
        <Stack alignItems="flex-end" width="100%">
          <Box
            sx={{
              paddingTop: 0.5,
              width: 250,
              paddingBottom: 0,
              marginBottom: 0,
            }}
          >
            <Select
              size="small"
              fullWidth
              label="Period"
              options={granularityOptions}
              value={granularity}
              onChange={(e) => {
                const g = e.target.value as TIME_GRANULARITY
                dispatch(
                  patchDraftFilters({
                    key: TAB_KEYS.COMPOSITIONS,
                    patch: { time_granularity: g },
                  })
                )
                dispatch(applyAndFetchChart({ key: TAB_KEYS.COMPOSITIONS }))
              }}
            />
          </Box>
        </Stack>
        {!hasAnyData ? (
          <EmptyState />
        ) : (
          <Box display="flex" justifyContent="center" width="100%">
            <Card
              data-exportable-chart="true"
              variant="outlined"
              sx={{
                ...sxStyles.parametersComparisonCard,
                width: '100%',
              }}
            >
              <CardHeader
                title={prettyAttr('Compositions')}
                titleTypographyProps={{ fontSize: 24, fontWeight: 800 }}
              />
              <CardContent sx={sxStyles.parametersComparisonCardContent}>
                <Box sx={{ flex: 1 }}>
                  <ChartExportWrapper title="Compositions">
                    <MultiLineChart
                      data={rows}
                      series={series}
                      xDataKey="period"
                      hideYAxisLabels={false}
                      xAxisTooltip={CompositionsTooltip}
                      usePercentageScale={true}
                    />
                  </ChartExportWrapper>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </TrendChartStatus>
    </DataTabsLayout>
  )
}
